import { acquireTokenForApi } from "@/authConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from 'react-bootstrap';
import { sanitizeHtml } from "@/utils/sanitizeHtml";
import SuccessModal from "@/components/SuccessModal";
import SignaturePad from "./SignaturePad";
import "./LegacySignatureFormPage.scss";

const SUCCESS_REDIRECT_URL = "https://intramax.enmax.com/resource/facilities/fitness_facilities"; 

type FormInstanceResponseDto = {
  form: {
    formId: number;
    formGuid: string;
    formTitle: string;
    formDescription: string;
    formCompleteMessage?: string | null;
    formFooter?: string | null;
    sections: Array<{
      formSectionId: number;
      formSectionGuid: string;
      sortOrder: number;
      sectionType: string;
      formSectionConfig: string;
      title?: string | null;
      description?: string | null;
      targetNext?: string | null;
      targetPrev?: string | null;
      buttonNext?: string | null;
      buttonPrev?: string | null;
    }>;
  };
  fields: Array<{
    formFieldId: number;
    formFieldGuid: string;
    sortOrder: number;
    formSectionGuid?: string | null;
    required: number;
    formFieldConfig: string;
    title?: string | null;
    subtitle?: string | null;
  }>;
  options: Array<{
    formFieldOptionId: number;
    formFieldId: number;
    formFieldOption: string;
    fieldType?: string | null;
    formFieldOptionGuid?: string | null;
  }>;
};

type LegacyResponse = {
  formFieldGuid: string;
  formFieldOptionGuid?: string | null;
  response: string;
};

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function safeJson<T>(value: string, fallback: T): T {
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}

export default function LegacySignatureFormPage() {
  const q = useQuery();
  const g = (q.get("g") ?? "").trim();

  const [data, setData] = useState<FormInstanceResponseDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // store per field: legacy-style collected responses
  const [responses, setResponses] = useState<Record<string, LegacyResponse[]>>({});
  const [requiredErrors, setRequiredErrors] = useState<Set<string>>(new Set());

  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string>("Submitted!");


  // wizard state
  const [activeSectionGuid, setActiveSectionGuid] = useState<string>("default");

  useEffect(() => {
    if (!g) {
      setLoading(false);
      setErr("No 'g' querystring provided. Example: /signatures/GetSignature?g=GUID");
      return;
    }

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        const token = await acquireTokenForApi();

        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/forms/legacy/${encodeURIComponent(g)}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (!res.ok) throw new Error(await res.text());

        const json = (await res.json()) as FormInstanceResponseDto;
        setData(json);

        // init empty arrays for each field
        const init: Record<string, LegacyResponse[]> = {};
        json.fields.forEach((f) => (init[f.formFieldGuid] = []));
        setResponses(init);

        // init wizard active section
        const sectionsSorted = [...(json.form.sections ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
        const firstGuid = sectionsSorted[0]?.formSectionGuid?.trim() || "default";
        setActiveSectionGuid(firstGuid);
      } catch (e: any) {
        setErr(e?.message ?? "Failed to load form.");
      } finally {
        setLoading(false);
      }
    })();
  }, [g]);

  function setFieldResponses(formFieldGuid: string, rows: LegacyResponse[]) {
    setResponses((prev) => ({ ...prev, [formFieldGuid]: rows }));
  }

  const optionsByFieldId = useMemo(() => {
    if (!data) return {} as Record<number, FormInstanceResponseDto["options"]>;
    return data.options.reduce((acc, o) => {
      (acc[o.formFieldId] ??= []).push(o);
      return acc;
    }, {} as Record<number, typeof data.options>);
  }, [data]);

  const sectionsSorted = useMemo(() => {
    if (!data) return [];
    // assume backend already adds "default" section if none exist
    return [...(data.form.sections ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
  }, [data]);

  const sectionByGuid = useMemo(() => {
    const m = new Map<string, (FormInstanceResponseDto["form"]["sections"][number])>();
    for (const s of sectionsSorted) m.set(s.formSectionGuid, s);
    return m;
  }, [sectionsSorted]);

  const sectionIndexByGuid = useMemo(() => {
    const m = new Map<string, number>();
    sectionsSorted.forEach((s, i) => m.set(s.formSectionGuid, i));
    return m;
  }, [sectionsSorted]);

  const fieldsBySectionGuid = useMemo(() => {
    const map = new Map<string, FormInstanceResponseDto["fields"]>();
    if (!data) return map;

    for (const f of data.fields) {
      const sg = (f.formSectionGuid?.trim() || "default");
      if (!map.has(sg)) map.set(sg, []);
      map.get(sg)!.push(f);
    }

    // sort fields within each section
    for (const [k, arr] of map.entries()) {
      arr.sort((a, b) => a.sortOrder - b.sortOrder);
      map.set(k, arr);
    }

    return map;
  }, [data]);

  function getNextGuid(currGuid: string): string | null {
    const curr = sectionByGuid.get(currGuid);
    const explicit = curr?.targetNext?.trim();
    if (explicit && sectionByGuid.has(explicit)) return explicit;

    const idx = sectionIndexByGuid.get(currGuid) ?? -1;
    const next = sectionsSorted[idx + 1];
    return next ? next.formSectionGuid : null;
  }

  function getPrevGuid(currGuid: string): string | null {
    const curr = sectionByGuid.get(currGuid);
    const explicit = curr?.targetPrev?.trim();
    if (explicit && sectionByGuid.has(explicit)) return explicit;

    const idx = sectionIndexByGuid.get(currGuid) ?? -1;
    const prev = sectionsSorted[idx - 1];
    return prev ? prev.formSectionGuid : null;
  }

  function validateSection(sectionGuid: string): Set<string> {
    const missing = new Set<string>();
    if (!data) return missing;

    const sectionFields = fieldsBySectionGuid.get(sectionGuid) ?? [];
    for (const f of sectionFields) {
      if (f.required !== 1) continue;
      const rows = responses[f.formFieldGuid] ?? [];
      const ok = rows.some((r) => (r.response ?? "").trim().length > 0);
      if (!ok) missing.add(f.formFieldGuid);
    }
    return missing;
  }

  async function submitAll() {
    if (!data || submitting) return;

    // validate all required fields across whole form before final submit
    const missing = new Set<string>();
    for (const f of data.fields) {
      if (f.required !== 1) continue;
      const rows = responses[f.formFieldGuid] ?? [];
      const ok = rows.some((r) => (r.response ?? "").trim().length > 0);
      if (!ok) missing.add(f.formFieldGuid);
    }

    setRequiredErrors(missing);

    if (missing.size > 0) {
      setErr(`${missing.size} required field(s) need to be filled before submitting.`);
      scrollToFirstError(missing);
      return;
    }

    setErr(null);
    setSubmitting(true);

    try {
      const allRows = Object.values(responses).flat();
      const payload = { formGuid: encodeURIComponent(g), responses: allRows };

      const token = await acquireTokenForApi();

      const res = await fetch(
        `${import.meta.env.VITE_API_URL}/api/forms/legacy/${encodeURIComponent(g)}/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        setErr(await res.text());
        return;
      }

      // ---- SUCCESS ----
      const msg = data.form.formCompleteMessage?.trim() || "Submitted!";
      setSuccessMessage(msg);
      setShowSuccess(true);
    } catch (e: any) {
      setErr(e?.message ?? "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  }


  function scrollToFirstError(missing: Set<string>) {
    const first = Array.from(missing)[0];
    if (!first) return;
    const el = document.getElementById(`field-${first}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  function onNext() {
    if (!data) return;

    const missing = validateSection(activeSectionGuid);
    if (missing.size > 0) {
      setRequiredErrors(missing);
      setErr(`${missing.size} required field(s) need to be filled before continuing.`);
      scrollToFirstError(missing);
      return;
    }

    setRequiredErrors(new Set());
    setErr(null);

    const next = getNextGuid(activeSectionGuid);
    if (next) setActiveSectionGuid(next);
  }

  function onBack() {
    const prev = getPrevGuid(activeSectionGuid);
    if (prev) {
      setRequiredErrors(new Set());
      setErr(null);
      setActiveSectionGuid(prev);
    }
  }

  if (loading) return <div className="container py-4">Loading…</div>;
  if (err && !data) return <div className="container py-4 alert alert-danger">{err}</div>;
  if (!data) return <div className="container py-4">Not found.</div>;


    type LegacyButtonSpec = {
        attr?: Record<string, string>;
        data?: Record<string, string>;
        text?: string;
    };

    function parseButtonSpec(raw?: string | null): LegacyButtonSpec | null {
        if (!raw) return null;
        try {
            const obj = JSON.parse(raw) as LegacyButtonSpec;
            if (!obj || typeof obj !== "object") return null;
            return obj;
        } catch {
            return null;
        }
    }

    function buttonPropsFromSpec(spec: LegacyButtonSpec | null) {
        const className = spec?.attr?.class ?? "";
        const disabled =
            /\bdisabled\b/i.test(className) ||
            spec?.attr?.disabled === "disabled" ||
            spec?.attr?.disabled === "true";

        // turn {"data":{"x":"y"}} into data-x="y"
        const dataAttrs: Record<string, string> = {};
        for (const [k, v] of Object.entries(spec?.data ?? {})) {
            dataAttrs[`data-${k}`] = v;
        }

        // allow a safe subset of attrs only (avoid onclick, style, etc.)
        const allowedAttrKeys = new Set(["class", "title", "aria-label", "type"]);
        const safeAttrs: Record<string, string> = {};
        for (const [k, v] of Object.entries(spec?.attr ?? {})) {
            if (allowedAttrKeys.has(k)) safeAttrs[k] = v;
        }

        return {
            className,
            disabled,
            text: spec?.text ?? undefined,
            dataAttrs,
            safeAttrs
        };
    }


  const activeSection = sectionByGuid.get(activeSectionGuid);
  const activeFields = fieldsBySectionGuid.get(activeSectionGuid) ?? [];

  const activeIndex = sectionIndexByGuid.get(activeSectionGuid) ?? 0;
  const totalSteps = sectionsSorted.length || 1;

  const prevGuid = getPrevGuid(activeSectionGuid);
  const nextGuid = getNextGuid(activeSectionGuid);
  const isLast = !nextGuid;

  const prevSpec = parseButtonSpec(activeSection?.buttonPrev);
    const nextSpec = parseButtonSpec(activeSection?.buttonNext);

    const prevBtn = buttonPropsFromSpec(prevSpec);
    const nextBtn = buttonPropsFromSpec(nextSpec);

    // fallback classes if no JSON provided
    const prevClass = prevBtn.className || "btn btn-outline-secondary";
    const nextClass = nextBtn.className || "btn btn-primary";

  return (
    <div className="container py-4 legacy-form">
      <div className="mb-3">
        <h1 className="h4 mb-1">{data.form.formTitle}</h1>
        {data.form.formDescription ? <div className="text-muted">{data.form.formDescription}</div> : null}
      </div>

      {/* Wizard progress */}
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="small text-muted">
          Step {activeIndex + 1} of {totalSteps}
        </div>
        <div className="small text-muted">{Math.round(((activeIndex + 1) / totalSteps) * 100)}%</div>
      </div>
      <div className="progress mb-3" role="progressbar" aria-valuenow={activeIndex + 1} aria-valuemin={0} aria-valuemax={totalSteps}>
        <div className="progress-bar" style={{ width: `${((activeIndex + 1) / totalSteps) * 100}%` }} />
      </div>

      <div className="card">
        <div className="card-body">
          {/* Section header */}
          {/*activeSection?.title ? (
            <div className="mb-3">
              <h2 className="h5 mb-1">{activeSection.title}</h2>
              {activeSection.description ? <div className="text-muted">{activeSection.description}</div> : null}
            </div>
          ) : null*/}

          {/* Step fields */}
          {activeFields.map((f) => {
            const cfg = safeJson<any>(f.formFieldConfig, {});
            const type = (cfg?.Type ?? "Text").toString();
            const isMissing = requiredErrors.has(f.formFieldGuid);

            // Wrapper to enable scroll-to-error
            const wrapStart = (children: React.ReactNode) => (
              <div
                id={`field-${f.formFieldGuid}`}
                key={f.formFieldGuid}
                className={"mb-4 " + (isMissing ? "border border-danger rounded" : "")}
              >
                {children}
              </div>
            );

            if (type === "Choice") {
              const other = !!cfg?.Other;
              const multiple = !!cfg?.Multiple;
              const opts = optionsByFieldId[f.formFieldId] ?? [];

              return wrapStart(
                <>
                    {f.title && f.subtitle ? (
                        <div className="legacy-field-label">
                            <span className="legacy-field-title">
                                {f.title}
                            </span>

                            {f.required === 1 ? (
                                <span className="legacy-required" title="Required" aria-label="Required">*</span>
                            ) : null}

                            {f.subtitle ? <div className="text-muted small mb-2">{f.subtitle}</div> : null}
                        </div>
                    ) : null}

                  <div className="legacy-choice-list">
                    {opts.map(o => {
                        const selected =
                        (responses[f.formFieldGuid] ?? []).some(
                            r => r.formFieldOptionGuid === o.formFieldOptionGuid
                        );

                        return (
                        <button
                            key={o.formFieldOptionId}
                            type="button"
                            className={"legacy-choice-item" + (selected ? " is-selected" : "")}
                            onClick={() => {
                            if (multiple) {
                                // optional later; for now you said single-select
                                return;
                            }

                            // toggle: click again clears selection
                            if (selected) {
                                setFieldResponses(f.formFieldGuid, []);
                                return;
                            }

                            setFieldResponses(f.formFieldGuid, [
                                {
                                formFieldGuid: f.formFieldGuid,
                                formFieldOptionGuid: o.formFieldOptionGuid ?? null,
                                response: o.formFieldOption
                                }
                            ]);
                            }}
                            aria-pressed={selected}
                        >
                            <span className="legacy-choice-dot" aria-hidden="true" />
                            <span className="legacy-choice-text">
                            {/* if option text can contain rich text, swap to dangerouslySetInnerHTML */}
                            {o.formFieldOption}
                            </span>
                        </button>
                        );
                    })}
                    </div>


                  {other ? <div className="text-muted small mt-2">(Other not enabled)</div> : null}
                </>
              );
            }

            if (type === "Signature") {
              return wrapStart(
                <>
                  <div
                    className="fw-semibold"
                    dangerouslySetInnerHTML={sanitizeHtml(f.title || "")}
                  />
                  {f.subtitle ? (
                    <div
                      className="text-muted small mb-2"
                      dangerouslySetInnerHTML={sanitizeHtml(f.subtitle || "")}
                    />
                  ) : null}

                  <SignaturePad
                    onChangeDataUrl={(url) => {
                      const htmlImg = url ? `<img src="${url}" />` : "";
                      const rows: LegacyResponse[] = htmlImg
                        ? [{ formFieldGuid: f.formFieldGuid, formFieldOptionGuid: null, response: htmlImg }]
                        : [];
                      setFieldResponses(f.formFieldGuid, rows);
                    }}
                  />
                </>
              );
            }

            if (type === "Paragraph") {
              // Paragraphs are informational content; no required validation
              return (
                <div key={f.formFieldGuid} className="mb-4">
                  {f.title ? <p dangerouslySetInnerHTML={sanitizeHtml(f.title || "")} /> : null}
                  {f.subtitle ? <p dangerouslySetInnerHTML={sanitizeHtml(f.subtitle || "")} /> : null}
                </div>
              );
            }

            // fallback: simple text input
            return wrapStart(
              <>
                <label className="form-label fw-semibold">
                  {f.title ?? "Field"} {f.required === 1 ? <span className="text-danger">*</span> : null}
                </label>
                {f.subtitle ? <div className="text-muted small mb-2">{f.subtitle}</div> : null}
                <input
                  className="form-control"
                  value={(responses[f.formFieldGuid]?.[0]?.response ?? "")}
                  onChange={(e) => {
                    const val = e.target.value ?? "";
                    const rows: LegacyResponse[] = val.trim().length
                      ? [{ formFieldGuid: f.formFieldGuid, formFieldOptionGuid: null, response: val }]
                      : [];
                    setFieldResponses(f.formFieldGuid, rows);
                  }}
                />
                <div className="text-muted small mt-1">Type: {type} (fallback)</div>
              </>
            );
          })}

          {err ? <div className="alert alert-danger text-center"><FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />{err}</div> : null}

          {/* Wizard nav */}
          <div className="d-flex gap-2 mt-3 justify-content-between">
            <button
                type="button"
                className={prevClass}
                disabled={!prevGuid || prevBtn.disabled}
                onClick={onBack}
                {...prevBtn.dataAttrs}
                {...(prevBtn.safeAttrs["title"] ? { title: prevBtn.safeAttrs["title"] } : {})}
                {...(prevBtn.safeAttrs["aria-label"] ? { "aria-label": prevBtn.safeAttrs["aria-label"] } : {})}
                >
                {prevBtn.text || "Back"}
                </button>

                {!isLast ? (
                <button
                    type="button"
                    className={nextClass}
                    disabled={nextBtn.disabled}
                    onClick={onNext}
                    {...nextBtn.dataAttrs}
                    {...(nextBtn.safeAttrs["title"] ? { title: nextBtn.safeAttrs["title"] } : {})}
                    {...(nextBtn.safeAttrs["aria-label"] ? { "aria-label": nextBtn.safeAttrs["aria-label"] } : {})}
                >
                    {nextBtn.text || "Next"}
                </button>
                ) : (
                <Button
                  variant="success"
                  onClick={submitAll}
                  disabled={submitting}
                >
                  {submitting ? "Submitting…" : "Submit"}
                </Button>
                )}
          </div>
        </div>
        <SuccessModal
          show={showSuccess}
          title="Submitted"
          message={successMessage}
          onRedirect={() => {
            window.location.href = SUCCESS_REDIRECT_URL;
          }}
          onClose={() => {
            window.location.href = SUCCESS_REDIRECT_URL;
          }}
          closeText="Close"
        />
      </div>

      {/*
      data.form.formFooter ? (
        <div className="text-muted small mt-3" dangerouslySetInnerHTML={sanitizeHtml(data.form.formFooter)} />
      ) : null
      */}
    </div>
  );
}
